/**
 * Corner-Popup (Figma 8710-24216).
 *
 * Das Markup kommt komplett aus Twig (_/components/corner-popup.twig) – hier
 * geht es nur um sichtbar/geschlossen. Das Popup startet per CSS unsichtbar und
 * wird erst hier freigegeben; auf Unterseiten liegt das Markup damit nur bereit
 * und bleibt verborgen. Ohne JS bleibt es über `html:not(.js)` auf der
 * Startseite sichtbar.
 *
 * Verhalten:
 *  - Das Popup erscheint nur auf der Startseite – auf Unterseiten stört es zu
 *    sehr. Das Markup steht trotzdem auf jeder Seite (geteiltes
 *    Blitz-Fragment), es bleibt dort nur verborgen.
 *  - Über dem Buchungs-Overlay entscheidet SHOW_WITH_BOOKING_OVERLAY (siehe
 *    unten). Steht der Schalter auf false, blendet das Overlay die Kachel aus –
 *    auch auf der Startseite, weil sie z-index-technisch über allem liegt
 *    (--corner-popup-z-index) und sonst mitten in der Buchung stünde.
 *  - Geschlossen wird über den Schließen-Button und den eigenen CTA. Der
 *    Zustand wird bewusst NICHT gemerkt: beim nächsten Aufruf der Startseite
 *    ist die Kachel wieder da.
 *  - Die Mega-Navigation lässt es bewusst unberührt.
 *
 * Das Element bleibt beim Schließen im DOM (nur `visibility: hidden`, damit es
 * aus Tab-Reihenfolge und Screenreader-Baum fällt) – nur so lässt es sich
 * später wieder einfahren.
 */

const VISIBLE_CLASS = "is-visible"

// MegaNav.vue spiegelt store.bookingOpen als Body-Klasse. Über den Store laufen
// ALLE Buchungs-Einstiege: Header-CTA, die `data-booking`-CTAs aus Twig, die
// Neuigkeiten und der Auto-Open per ?open=booking. Deshalb am Zustand
// angesetzt und nicht an einzelnen Links. Bewusst NICHT `nav-locked`: das gilt
// auch für die offene Navigation.
const BOOKING_CLASS = "booking-open"

function isBookingOpen() {
    return document.body.classList.contains(BOOKING_CLASS)
}

// Der Section-Handle steht als Body-Klasse am Dokument (siehe
// _/layout/default.twig) und ist sprachunabhängig – die Erkennung greift damit
// auch auf /en. Bewusst hier und nicht in Twig entschieden: das Popup ist ein
// geteiltes Blitz-Fragment (includeCached) und kennt den Seitenkontext nicht.
const HOMEPAGE_CLASS = "page-section-homepage"

function isHomepage() {
    return document.body.classList.contains(HOMEPAGE_CLASS)
}

// Soll die Kachel auch über dem Buchungs-Overlay stehen? Bewusst ein Schalter
// im Code und (noch) kein CMS-Feld: die Entscheidung fällt bisher redaktionell
// pro Kampagne, nicht pro Redakteur.
//
//   false – das Overlay blendet die Kachel aus, auf JEDER Seite. Sie bleibt
//           damit ausschließlich der Startseite vorbehalten.
//   true  – die Kachel fährt mit dem Overlay ein, auch auf Unterseiten, und
//           auch dann, wenn sie vorher weggeklickt wurde.
//
// Wandert das irgendwann ins CMS, ersetzt ein data-Attribut am Popup-Markup
// diese Konstante – die Auswertung in shouldShow() bleibt dieselbe.
const SHOW_WITH_BOOKING_OVERLAY = false

function initPopup(root) {
    // Vom Nutzer weggeklickt. Gilt nur für diesen Seitenaufruf; mit
    // SHOW_WITH_BOOKING_OVERLAY hebt ein neuer Buchungs-Einstieg es wieder auf
    // (siehe Observer).
    let dismissed = false

    // Flankenerkennung für das Buchungs-Overlay: der Observer läuft bei JEDER
    // Body-Klassenänderung (nav-locked, …), interessant ist aber nur der
    // Moment, in dem das Overlay aufgeht.
    let bookingWasOpen = isBookingOpen()

    // Solange das Overlay offen ist, entscheidet allein der Schalter – sonst
    // die Seite. Damit deckt false den Fall „Startseite, aber Overlay liegt
    // darüber" mit ab.
    const shouldShow = () => {
        if (dismissed) return false
        if (isBookingOpen()) return SHOW_WITH_BOOKING_OVERLAY

        return isHomepage()
    }

    // Einzige Stelle, die über sichtbar/unsichtbar entscheidet. Der Zustand
    // wird jedes Mal neu aus den Body-Klassen berechnet, statt aus einzelnen
    // Ereignissen fortgeschrieben zu werden – sonst hängt die Kachel früher
    // oder später in einem Zustand fest, den niemand mehr aufräumt.
    const sync = () => {
        root.classList.toggle(VISIBLE_CLASS, shouldShow())
    }

    const dismiss = () => {
        dismissed = true
        sync()
    }

    // Sowohl das X als auch der CTA-Wrapper tragen den Hook: ein Klick auf den
    // CTA schließt das Popup mit. Der Link navigiert dabei normal weiter (kein
    // preventDefault).
    root.querySelectorAll("[data-corner-popup-close]").forEach((el) => {
        el.addEventListener("click", dismiss)
    })

    // Kein modaler Dialog (kein Fokus-Trap) – Escape schließt daher nur, wenn
    // der Fokus tatsächlich im Popup liegt. Sonst würde es der MegaNav das
    // Escape wegnehmen.
    root.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            event.stopPropagation()
            dismiss()
        }
    })

    // Zustandswechsel des Buchungs-Overlays nachziehen. Der Delay steckt in der
    // CSS-Transition von .is-visible, die Kachel kommt also kurz nach dem
    // Overlay; das Ausfahren läuft über die Basis-Regel ohne Delay und damit
    // zeitgleich mit ihm.
    const bookingObserver = new MutationObserver(() => {
        const bookingOpen = isBookingOpen()

        // Nur die steigende Flanke, und nur wenn die Kachel im Overlay
        // überhaupt erwünscht ist: dann holt sie jeder neue Buchungs-Einstieg
        // zurück, auch wenn sie vorher weggeklickt wurde.
        if (SHOW_WITH_BOOKING_OVERLAY && bookingOpen && !bookingWasOpen) {
            dismissed = false
        }
        bookingWasOpen = bookingOpen

        sync()
    })
    bookingObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
    })

    // Startzustand nach denselben Regeln (?open=booking öffnet das Overlay
    // bereits beim Seitenaufbau). Doppeltes rAF, damit der Browser den
    // Ausgangszustand (opacity 0 + translateY) committet und die
    // Einblend-Transition läuft.
    requestAnimationFrame(() => requestAnimationFrame(sync))
}

export function initCornerPopup() {
    document.querySelectorAll("[data-corner-popup]").forEach(initPopup)
}
